import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ConversationStatus, 
  allStatuses, 
  defaultVisibleStatuses, 
  getStatusConfig 
} from "@/types/conversation";
import { StatusBadge } from "./status-badge";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatusFilterProps {
  selectedStatuses: ConversationStatus[];
  onStatusChange: (statuses: ConversationStatus[]) => void;
  className?: string;
}

export function StatusFilter({
  selectedStatuses,
  onStatusChange,
  className
}: StatusFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasInitializedRef = useRef(false);

  // Initialize with default statuses if none are selected (only once)
  useEffect(() => {
    if (selectedStatuses.length === 0 && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      onStatusChange(defaultVisibleStatuses);
    } else if (selectedStatuses.length > 0) {
      hasInitializedRef.current = true;
    }
  }, [selectedStatuses.length, onStatusChange]);

  const handleStatusToggle = (status: ConversationStatus) => {
    const newSelection = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];
    
    onStatusChange(newSelection);
  };

  const handleSelectAll = () => {
    onStatusChange(allStatuses);
  };

  const handleSelectNone = () => {
    onStatusChange([]);
  };

  const handleSelectDefaults = () => {
    onStatusChange(defaultVisibleStatuses);
  };

  const isAllSelected = selectedStatuses.length === allStatuses.length;
  const isDefaultsSelected = 
    selectedStatuses.length === defaultVisibleStatuses.length &&
    defaultVisibleStatuses.every(status => selectedStatuses.includes(status));

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 border-gray-300 bg-white hover:bg-gray-50",
            className
          )}
        >
          <Filter className="h-4 w-4 mr-2" />
          Status
          {selectedStatuses.length > 0 && selectedStatuses.length < allStatuses.length && (
            <Badge
              variant="secondary"
              className="ml-2 bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 h-5"
            >
              {selectedStatuses.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">Filter by Status</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Quick actions */}
          <div className="flex gap-2 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectDefaults}
              className="h-7 text-xs px-2"
              disabled={isDefaultsSelected}
            >
              Default
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-7 text-xs px-2"
              disabled={isAllSelected}
            >
              All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectNone}
              className="h-7 text-xs px-2"
              disabled={selectedStatuses.length === 0}
            >
              None
            </Button>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {allStatuses.map((status) => {
            const isSelected = selectedStatuses.includes(status);
            const config = getStatusConfig(status);
            
            return (
              <div
                key={status}
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                onClick={() => handleStatusToggle(status)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleStatusToggle(status)}
                  className="pointer-events-none"
                />
                <StatusBadge 
                  status={status} 
                  size="sm"
                  className="pointer-events-none"
                />
              </div>
            );
          })}
        </div>

        {/* Selected count footer */}
        <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-600">
          {selectedStatuses.length} of {allStatuses.length} status{allStatuses.length !== 1 ? 'es' : ''} selected
        </div>
      </PopoverContent>
    </Popover>
  );
} 